import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { IconPicker } from './IconPicker';
import { getBreadcrumbItems } from './breadcrumb-items';
import { getAdminBreadcrumbItems } from './admin/admin-nav';
import { usePageTitle } from '../hooks/usePageTitle';
import { AdminBreadcrumb } from './admin/AdminBreadcrumb';

interface BreadcrumbProps {
  documentTitle?: string;
  projectTitle?: string;
  documentIcon?: string | null;
  onIconChange?: (icon: string | null) => void;
  isReadOnly?: boolean;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ documentTitle, projectTitle, documentIcon, onIconChange, isReadOnly }) => {
  const location = useLocation();
  const { t } = useLocale();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const items = isAdminRoute
    ? getAdminBreadcrumbItems(location.pathname).map((item) => t(item.labelKey))
    : getBreadcrumbItems(location.pathname, t, { documentTitle, projectTitle });

  usePageTitle(...items);

  if (isAdminRoute) {
    return <AdminBreadcrumb />;
  }

  if (items.length === 0) {
    return null;
  }

  const showIconPicker = location.pathname.includes('/documents/') && documentIcon !== undefined && onIconChange;

  return (
    <nav className="flex items-center gap-2">
      {showIconPicker && (
        <IconPicker
          selectedIcon={documentIcon}
          onSelectIcon={onIconChange}
          disabled={isReadOnly}
        />
      )}
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span style={{ color: 'var(--muted-foreground)', fontSize: '14px' }}> / </span>}
          <span
            style={{
              fontSize: index === 0 ? '18px' : '14px',
              fontWeight: index === 0 ? 800 : 400,
              color: index === 0 ? 'var(--foreground)' : 'var(--muted-foreground)',
            }}
          >
            {item}
          </span>
        </React.Fragment>
      ))}
    </nav>
  );
};
